"use client";
import { sendSlackMessageNoAuth } from "@/components/ui/utils";
import { SlackNotificationType } from "@/lib/shared/types";
import JSConfetti from "js-confetti";
import { useCallback, useEffect, useState, Suspense } from "react";
import { VALID_WORDS } from "./constants";
import { useSearchParams } from "next/navigation";
import { downloadCertificate, getDayOfWeek, formatTime } from "@/components/ui/games/GameCertificate";
import { OtherGames } from "@/components/ui/games/OtherGames";

interface GuessResult {
  letter: string;
  status: "correct" | "present" | "absent";
}

interface Guess {
  guess: string;
  result: GuessResult[];
}

function WordlePageContent() {
  const searchParams = useSearchParams();
  const isClubhouse = searchParams?.get("id") === "clubhouse";
  const [playerName, setPlayerName] = useState<string>("");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [gameState, setGameState] = useState<"playing" | "won" | "lost">("playing");
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [shakeRow, setShakeRow] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>("");
  const [wordLoading, setWordLoading] = useState<boolean>(true);
  const [shareMessage, setShareMessage] = useState<string>("");

  const QWERTY_ROWS = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
  ];

  // Fetch word on component mount
  useEffect(() => {
    const fetchWord = async () => {
      try {
        const response = await fetch("/api/wordle/get-word");
        const data = await response.json();

        if (data.success) {
          setAnswer(data.word);
        } else {
          console.error("Failed to fetch word");
        }
      } catch (error) {
        console.error("Error fetching word:", error);
      } finally {
        setWordLoading(false);
      }
    };

    fetchWord();
  }, []);

  // Live timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (startTime && gameState === "playing") {
      interval = setInterval(() => {
        setCurrentTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameState]);

  const startGame = () => {
    if (playerName.trim().length < 2) {
      setErrorMessage("Please enter your name (at least 2 characters)");
      return;
    }
    setGameStarted(true);
    setStartTime(Date.now());
    setErrorMessage("");
  };

  const checkGuess = (guess: string): GuessResult[] => {
    const result: GuessResult[] = [];
    const guessLetters = guess.toUpperCase().split("");
    const answerLetters = answer.split("");

    const availableLetters: { [key: string]: number } = {};

    for (let i = 0; i < 5; i++) {
      if (guessLetters[i] === answerLetters[i]) {
        result[i] = { letter: guessLetters[i], status: "correct" };
      } else {
        result[i] = { letter: guessLetters[i], status: "absent" };
        availableLetters[answerLetters[i]] = (availableLetters[answerLetters[i]] || 0) + 1;
      }
    }

    for (let i = 0; i < 5; i++) {
      if (result[i].status === "absent") {
        const letter = guessLetters[i];
        if (availableLetters[letter] > 0) {
          result[i].status = "present";
          availableLetters[letter]--;
        }
      }
    }

    return result;
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShakeRow(true);
    setTimeout(() => {
      setErrorMessage("");
      setShakeRow(false);
    }, 2000);
  };

  const saveResult = async (attempts: number, timeSeconds: number, won: boolean) => {
    setSaving(true);
    try {
      const response = await fetch("/api/wordle/save-result", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: playerName,
          tries: attempts,
          word_of_day: answer,
          time_seconds: timeSeconds,
          won: won,
        }),
      });

      if (!response.ok) {
        console.error("Failed to save result");
      }
    } catch (error) {
      console.error("Error saving result:", error);
    } finally {
      setSaving(false);
    }
  };

  const submitGuess = useCallback(() => {
    if (currentGuess.length !== 5) {
      showError("Word must be 5 letters long");
      return;
    }

    if (gameState !== "playing") return;

    if (!VALID_WORDS.has(currentGuess.toLowerCase())) {
      showError("Not a valid word");
      return;
    }

    const result = checkGuess(currentGuess);
    const newGuess = { guess: currentGuess.toUpperCase(), result };
    const newGuesses = [...guesses, newGuess];

    setGuesses(newGuesses);

    const isCorrect = result.every((r) => r.status === "correct");
    const finalTime = Date.now();
    const timeSeconds = Math.floor((finalTime - (startTime || 0)) / 1000);

    if (isCorrect) {
      setGameState("won");
      setEndTime(finalTime);
      saveResult(newGuesses.length, timeSeconds, true);
      const jsConfetti = new JSConfetti();
      jsConfetti.addConfetti();
      setTimeout(() => {
        handleDownloadCertificate(newGuesses.length, timeSeconds);
      }, 1000);
    } else if (newGuesses.length >= 6) {
      setGameState("lost");
      setEndTime(finalTime);
      saveResult(newGuesses.length, timeSeconds, false);
    }

    setCurrentGuess("");
  }, [currentGuess, gameState, guesses, startTime, playerName]);

  const handleKeyPress = useCallback(
    (key: string) => {
      if (gameState !== "playing") return;

      if (key === "ENTER") {
        submitGuess();
      } else if (key === "BACKSPACE") {
        setCurrentGuess((prev) => prev.slice(0, -1));
      } else if (key.length === 1 && currentGuess.length < 5) {
        setCurrentGuess((prev) => prev + key.toUpperCase());
      }
    },
    [gameState, currentGuess, submitGuess]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted) return;

      if (e.key === "Enter") {
        handleKeyPress("ENTER");
      } else if (e.key === "Backspace") {
        handleKeyPress("BACKSPACE");
      } else if (e.key.match(/^[a-zA-Z]$/)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyPress, gameStarted]);

  const getKeyStatus = (letter: string): "correct" | "present" | "absent" | "unused" => {
    for (const guess of guesses) {
      for (const result of guess.result) {
        if (result.letter === letter) {
          if (result.status === "correct") return "correct";
          if (result.status === "present") return "present";
          if (result.status === "absent") return "absent";
        }
      }
    }
    return "unused";
  };

  const getCellClass = (status: string, isCurrentRow: boolean, hasLetter: boolean) => {
    let baseClass =
      "w-14 h-14 border-2 flex items-center justify-center text-xl font-bold transition-all duration-200 rounded-lg";

    if (status) {
      switch (status) {
        case "correct":
          return `${baseClass} bg-green-200 text-green-800 border-green-300`;
        case "present":
          return `${baseClass} bg-yellow-200 text-yellow-800 border-yellow-300`;
        case "absent":
          return `${baseClass} bg-indigo-100 text-indigo-600 border-indigo-200`;
      }
    }

    if (isCurrentRow && hasLetter) {
      return `${baseClass} bg-indigo-50 border-indigo-400 border-2`;
    }

    if (isCurrentRow) {
      return `${baseClass} bg-indigo-50 border-indigo-300 border-2`;
    }

    return `${baseClass} bg-indigo-50 border-indigo-200`;
  };

  const getKeyClass = (status: string) => {
    switch (status) {
      case "correct":
        return "bg-green-200 text-green-800";
      case "present":
        return "bg-yellow-200 text-yellow-800";
      case "absent":
        return "bg-indigo-200 text-indigo-600";
      default:
        return "bg-indigo-100 hover:bg-indigo-200";
    }
  };

  const handleDownloadCertificate = async (attemptCount?: number, timeSeconds?: number) => {
    await downloadCertificate(
      {
        gameName: "Nowadays Times Wordle",
        playerName: playerName,
        stats: [getDayOfWeek(), `${attemptCount || guesses.length} attempts`, formatTime(timeSeconds || currentTime)],
        subtitle: "Wordle Champion!",
      },
      `wordle-${playerName}-${getDayOfWeek().toLowerCase()}.png`
    );
  };

  const generateShareText = useCallback(() => {
    // Get today's date in a readable format
    const today = new Date();
    const dateString = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    const attempts = gameState === "won" ? guesses.length : "X";
    let shareText = `Wordle ${dateString} ${attempts}/6\n\n`;

    // Generate emoji grid
    guesses.forEach((guess) => {
      let line = "";
      guess.result.forEach((result) => {
        if (result.status === "correct") {
          line += "🟩";
        } else if (result.status === "present") {
          line += "🟨";
        } else {
          line += "⬛";
        }
      });
      shareText += line + "\n";
    });

    shareText += "\n\nPlay at nowadays.ai/games/wordle";

    return shareText.trim();
  }, [guesses, gameState]);

  const handleShare = async () => {
    const shareText = generateShareText();

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage("Results copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setShareMessage("Failed to copy to clipboard");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  // Show loading while fetching word
  if (wordLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-black">Wordle</h1>
          <p className="text-base text-gray-600">Loading today's word...</p>
        </div>
      </div>
    );
  }

  if (!gameStarted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">Wordle</h1>
          <p className="text-lg text-gray-600 mb-2">Test your word-guessing skills</p>
          <p className="text-base text-gray-500">{getDayOfWeek()}</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="space-y-6">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-6 py-4 text-base border-2 border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && startGame()}
              autoFocus
            />

            {errorMessage && <div className="text-red-600 text-center font-medium">{errorMessage}</div>}

            <button
              onClick={startGame}
              className="w-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 text-base rounded-xl font-bold transition-all transform hover:scale-105 shadow-sm"
            >
              Start Playing →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
      {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-6" />}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-3 text-black">Wordle</h1>
          <p className="text-base text-gray-600">Hello {playerName}! Guess the 5-letter word</p>

          {/* Live Stats */}
          <div className="flex justify-center gap-8 mt-4 text-base font-semibold">
            <span className="text-gray-700">Attempts: {guesses.length}/6</span>
            <span className="text-gray-600">Time: {formatTime(currentTime)}</span>
          </div>
        </div>

        {errorMessage && <div className="text-red-600 font-semibold text-center mb-3 text-sm">{errorMessage}</div>}

        {gameState === "won" && (
          <div className="text-green-600 font-semibold text-center mb-3">
            <p>Congratulations! You solved it!</p>
            <p className="text-sm">
              {guesses.length} attempts • {formatTime(currentTime)}
            </p>
          </div>
        )}

        {gameState === "lost" && (
          <div className="text-red-600 font-semibold text-center mb-3">
            <p>
              Game over! The word was: <span className="font-bold">{answer}</span>
            </p>
          </div>
        )}

        {/* Game Grid */}
        <div className="grid grid-rows-6 gap-2 mb-6">
          {Array.from({ length: 6 }, (_, rowIndex) => {
            const guess = guesses[rowIndex];
            const isCurrentRow = rowIndex === guesses.length && gameState === "playing";

            return (
              <div
                key={rowIndex}
                className={`grid grid-cols-5 gap-2 ${shakeRow && isCurrentRow ? "animate-pulse" : ""}`}
              >
                {Array.from({ length: 5 }, (_, colIndex) => {
                  let letter = "";
                  let status = "";

                  if (guess) {
                    letter = guess.result[colIndex].letter;
                    status = guess.result[colIndex].status;
                  } else if (isCurrentRow && currentGuess[colIndex]) {
                    letter = currentGuess[colIndex];
                  }

                  const hasLetter = Boolean(letter);

                  return (
                    <div key={colIndex} className={getCellClass(status, isCurrentRow, hasLetter)}>
                      {letter}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Keyboard */}
        <div className="space-y-2">
          {QWERTY_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} className="flex justify-center gap-2">
              {row.map((key) => {
                const isSpecial = key === "ENTER" || key === "BACKSPACE";
                const status = isSpecial ? "unused" : getKeyStatus(key);
                const keyClasses = `${isSpecial ? "px-4 text-sm" : "w-10 text-sm"} h-12 rounded-lg font-semibold ${getKeyClass(status)} transition-colors hover:opacity-80 active:scale-95`;

                return (
                  <button
                    key={key}
                    onClick={() => handleKeyPress(key)}
                    className={keyClasses}
                    disabled={gameState !== "playing"}
                  >
                    {key === "BACKSPACE" ? "⌫" : key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Share and Certificate Section */}
        {(gameState === "won" || gameState === "lost") && (
          <>
            <div className="mt-6 mb-8 text-center">
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleShare}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Share Results
                </button>
                {gameState === "won" && (
                  <button
                    onClick={() => handleDownloadCertificate()}
                    className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm disabled:opacity-50 disabled:transform-none"
                    disabled={saving}
                  >
                    Download Certificate
                  </button>
                )}
              </div>
              {shareMessage && <div className="mt-3 text-green-600 font-medium text-sm">{shareMessage}</div>}
            </div>

            {/* Other Games Section */}
            <OtherGames currentGameId="wordle" />
          </>
        )}
      </div>
    </div>
  );
}

export default function WordlePage() {
  return (
    <Suspense
      fallback={
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <h1 className="text-2xl font-bold mb-4">Nowadays Times Wordle</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      }
    >
      <WordlePageContent />
    </Suspense>
  );
}
